import { isNodeCollection } from '../utils/dom';
import ListenerEvent from '../ListenerEvent';

/**
 * Base 'abstract' DOM scripting controller with common functionality for all
 * other controllers.
 *
 * All child controllers should have an `_init` method, that potentially does
 * custom stuff specifically for that controller, then calls `_initBase`:
 *
 * class SpecialController extends BaseController {
 *   _init(controllerConstructor[, other custom args]) {
 *     [custom stuff here]
 *
 *     // Finish with this, which will call the DOM-ready `init`
 *     this._initBase(controllerConstructor);
 *   }
 * }
 */
export default class BaseController {
  /**
   * Controller ID.
   *
   * Must be set in child controllers, preferably to the same as the class name.
   * This is used by the initializer (and possibly others) to check which
   * controller is used, since the actual name will be mangled when minified.
   *
   * @member {?string}
   * @static
   */
  static _baseId = null;

  /**
   * The controller constructor function.
   *
   * @member {Function}
   */
  _controllerConstructor;

  /**
   * In-memory state store.
   *
   * @member {Object}
   */
  _state = {};

  /**
   * Custom event listeners.
   *
   * @see _addListener()
   * @member {WeakMap}
   */
  _listeners = new WeakMap();

  /**
   * Set base data.
   *
   * @param {Function} controllerConstructor - The controller class being
   *   instantiated.
   */
  _initBase(controllerConstructor) {
    this._controllerConstructor = controllerConstructor;
    this.init();
  }

  /**
   * Called on DOM ready.
   */
  init() {
    // Override in subclass
  }

  /**
   * Called on window load (all resources have loaded).
   */
  load() {
    // Override in subclass
  }

  /**
   * Bind an event listener to an element.
   *
   * Keeps track of the wrapped custom listeners created in `_bind` or
   * `_delegate` that is required for the custom event object and `event.detail`
   * when dispatching custom events. Since the custom listener is a new
   * function, calling `removeEventListener` in `off()` with the original
   * listener won't work.
   *
   * @param {Object} el - DOM node.
   * @param {string} eventName - Name of the event to bind.
   * @param {Function} suppliedListener - The listener supplied to `on`.
   * @param {Function} customListener - The custom listener created in `_bind`
   *   or `_delegate`.
   * @param {boolean} [useCapture] - The third addEventListener param.
   */
  _addListener(
    el,
    eventName,
    suppliedListener,
    customListener,
    useCapture = false,
  ) {
    this._listeners.set(suppliedListener, customListener);
    el.addEventListener(eventName, customListener, useCapture);
  }

  /**
   * Remove an event listener from an element.
   *
   * @see _addListener()
   * @param {Object} el - DOM node.
   * @param {string} eventName - Name of the event to bind.
   * @param {Function} suppliedListener - The listener supplied to `on`.
   * @param {boolean} [useCapture] - The third removeEventListener param, must
   *   be the same as supplied to addEventListener.
   */
  _removeListener(el, eventName, suppliedListener, useCapture = false) {
    const listener = this._listeners.get(suppliedListener) || suppliedListener;
    el.removeEventListener(eventName, listener, useCapture);
    this._listeners.delete(suppliedListener);
  }

  /**
   * Bind a regular event listener to an element.
   *
   * @param {Object} el - The DOM node.
   * @param {string} eventName - Name of the event to bind.
   * @param {Function} listener - Event listener.
   * @param {boolean} [useCapture] - If the listener should trigger on the
   *   capture phase, defaults to false.
   */
  _bind(el, eventName, listener, useCapture = false) {
    const customListener = (event) => {
      listener(
        new ListenerEvent(event),
        event instanceof CustomEvent ? event.detail : undefined,
      );
    };
    this._addListener(el, eventName, listener, customListener, useCapture);
  }

  /**
   * Bind an event listener that delegates to the specified selector.
   *
   * @param {Object} el - The DOM node.
   * @param {string} eventName - Name of the event to bind.
   * @param {string} delegate - Selector to delegate to.
   * @param {Function} listener - Event listener.
   * @param {boolean} [useCapture] - If the listener should trigger on the
   *   capture phase, defaults to false.
   */
  _delegate(el, eventName, delegate, listener, useCapture = false) {
    const customListener = (event) => {
      let { target } = event;
      while (target) {
        if (target.matches(delegate)) {
          listener(
            new ListenerEvent(event, target),
            event instanceof CustomEvent ? event.detail : undefined,
          );
          return;
        }
        target =
          target !== event.currentTarget && target !== document.body
            ? target.parentNode
            : null;
      }
    };
    this._addListener(el, eventName, listener, customListener, useCapture);
  }

  /**
   * Bind an event, either directly or with delegation to a selector.
   *
   * If passing an array or a NodeList, the event will be bound to each Node.
   * Depending on the use case, consider switching to the delegation model
   * instead.
   *
   * @param {Node|NodeList|Array.<Node>} el - The DOM node(s) to bind the
   *   event on.
   * @param {string} eventName - Name of the event ('click', 'change'...).
   * @param {string|Function} delegateOrListener - Selector to delegate to, or
   *   event listener when not using delegation.
   * @param {Function} listener - Event listener when using delegation.
   * @param {boolean} [useCapture] - If the listener should trigger on the
   *   capture phase, defaults to false.
   * @return {Object} The passed in DOM node(s).
   * @example
   *
   * // Bind a click handler to a <button>.
   * on(buttonNode, 'click', (e) => {...})
   *
   * @example
   *
   * // Bind a click handler to a <nav> that is triggered whenever an element
   * // with the .nav-link class inside it is clicked.
   * on(navNode, 'click', '.nav-link', (e) => {...})
   */
  on(el, eventName, delegateOrListener, listener, useCapture = false) {
    // Validate signature on dev
    if (process.env.NODE_ENV === 'development') {
      if (!eventName || typeof eventName !== 'string') {
        throw new TypeError('The event name must be a string');
      }
      if (!el || typeof el !== 'object') {
        throw new TypeError(`${eventName} event must be bound to a DOM node`);
      }
      // Regular binding version
      if (!listener && typeof delegateOrListener !== 'function') {
        throw new TypeError(
          `The ${eventName} event listener must be a function`,
        );
      }
      // Delegation version
      if (
        listener &&
        (typeof delegateOrListener !== 'string' ||
          typeof listener !== 'function')
      ) {
        throw new TypeError(
          `Delegation for ${eventName} event must a selector followed by a listener function`,
        );
      }
    }

    if (isNodeCollection(el)) {
      Array.from(el).forEach((collectionEl) => {
        this.on(
          collectionEl,
          eventName,
          delegateOrListener,
          listener,
          useCapture,
        );
      });
      return el;
    }

    if (typeof delegateOrListener === 'function') {
      this._bind(el, eventName, delegateOrListener, useCapture);
    } else {
      this._delegate(el, eventName, delegateOrListener, listener, useCapture);
    }
    return el;
  }

  /**
   * Remove an event listener from an element.
   *
   * @param {Object} el - The DOM node.
   * @param {string} eventName - Name of the event to remove.
   * @param {Function} listener - Event listener that was attached with `on`.
   * @param {boolean} [useCapture] - If the listener was bound to the capture
   *   phase, defaults to false.
   * @return {Object} The passed in DOM node.
   */
  off(el, eventName, listener, useCapture = false) {
    if (isNodeCollection(el)) {
      Array.from(el).forEach((collectionEl) => {
        this.off(collectionEl, eventName, listener, useCapture);
      });
      return el;
    }

    this._removeListener(el, eventName, listener, useCapture);
    return el;
  }

  /**
   * Trigger a custom event.
   *
   * @param {string} eventName - Name of the event to trigger.
   * @param {mixed} [extraData] - Any extra data to pass to the listener.
   * @param {Object} [target] - DOM node to trigger the event on, defaults
   *   to the document.
   * @example
   *
   * triggerCustomEvent('modalOpen', modalData)
   */
  triggerCustomEvent(eventName, extraData, target = document) {
    target.dispatchEvent(new CustomEvent(eventName, { detail: extraData }));
  }

  /**
   * Bind a listener for a custom event.
   *
   * Just a shortcut to using `on` that matches the signature for
   * `triggerCustomEvent`.
   *
   * @param {string} eventName - Name of the custom event.
   * @param {Function} listener - Event listener.
   * @param {Object} [target] - DOM node to listen for the event on, defaults
   *   to the document.
   * @example
   *
   * onCustomEvent('modalOpen', (modalData) => {...})
   */
  onCustomEvent(eventName, listener, target = document) {
    this.on(target, eventName, listener);
  }

  /**
   * Set new state properties.
   *
   * @param {Object} newState - State object that will be merged into the
   *   existing state.
   */
  setState(newState) {
    if (process.env.NODE_ENV === 'development') {
      if (
        !newState ||
        typeof newState !== 'object' ||
        Object.keys(newState).length === 0
      ) {
        throw new Error('The new state must be a plain object');
      }
    }
    this._state = Object.assign(this._state, newState);
  }

  // eslint-disable-next-line jsdoc/require-param
  /**
   * Prevent the state from being mutated directly.
   *
   * @ignore
   */
  set state(_) {
    throw new Error("Don't set state directly, use setState");
  }

  /**
   * Return the state object.
   *
   * @ignore
   */
  get state() {
    return this._state;
  }
}
